import type { Step } from "react-joyride";

/**
 * Poll the DOM until `target` matches an element with real layout, or
 * `timeoutMs` elapses. Resolves either way — callers should treat the
 * timeout case as "best effort, proceed anyway".
 *
 * "Real layout" = non-zero width and height. This guards against the
 * Chakra Drawer / Modal pattern where the element exists in the DOM but
 * still has 0×0 dimensions mid-animation; Joyride would otherwise measure
 * it at the wrong position.
 *
 * Non-string targets (HTMLElement refs, undefined) resolve immediately.
 */
export const waitForTarget = (
	target: Step["target"] | undefined,
	timeoutMs = 1500,
	pollIntervalMs = 50,
): Promise<void> => {
	return new Promise((resolve) => {
		if (typeof target !== "string") {
			resolve();
			return;
		}

		const isReady = (): boolean => {
			const el = document.querySelector(target);
			if (!el) {
				return false;
			}
			const rect = (el as HTMLElement).getBoundingClientRect();
			return rect.width > 0 && rect.height > 0;
		};

		if (isReady()) {
			resolve();
			return;
		}

		const start = Date.now();
		const interval = window.setInterval(() => {
			if (isReady() || Date.now() - start > timeoutMs) {
				window.clearInterval(interval);
				resolve();
			}
		}, pollIntervalMs);
	});
};
